import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Box,
  Paper,
} from "@mui/material";

import { HeadingTwo } from "../Common/Components";
import { Link, useNavigate } from "react-router-dom";
import {
  MdKeyboardArrowLeft,
  MdOutlineNavigateBefore,
  MdOutlineNavigateNext,
} from "react-icons/md";
import { Col, Row } from "react-bootstrap";
import swal from "sweetalert2";

dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Fetch AU public holidays and filter VIC
async function fetchVictorianHolidays(year) {
  try {
    const res = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/AU`);
    if (!res.ok) throw new Error("Failed to fetch AU holidays");
    const data = await res.json();

    const vicHolidays = data.filter(h => !h.counties || h.counties.includes("AU-VIC"));

    return vicHolidays.map(h => ({
      date: h.date,
      name: h.localName || h.name,
      isPublicHoliday: true,
      source: "nager"
    }));
  } catch (err) {
    console.error("Victorian Holiday API Error:", err);
    return [];
  }
}

export default function HolidayCalendar() {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [holidays, setHolidays] = useState([]);
  const navigate = useNavigate();
  const currentYear = dayjs().year();

  useEffect(() => {
    let mounted = true;
    const year = currentMonth.year();

    const load = async () => {
      // Fetch from backend first (GET API)
      try {
        const res = await fetch(`${API_BASE_URL}fetch/${year}`, {
            headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") }
        });
        if (!res.ok) throw new Error("Failed to fetch from DB");
        const dbHolidays = await res.json();
        if (mounted && dbHolidays?.holidays?.length) {
            setHolidays(dbHolidays?.holidays);
        }else {
            const list = await fetchVictorianHolidays(year);
            setHolidays(list);
        }
      } catch (err) {
        console.error("Fetch from DB failed, falling back to Nager API", err);
        const list = await fetchVictorianHolidays(year);
        if (mounted) setHolidays(list);
      }
    };

    load();
    return () => { mounted = false; };
  }, [currentMonth.year()]);

  const startOfMonth = currentMonth.startOf("month");
  const endOfMonth = currentMonth.endOf("month");

  const days = [];
  const startDate = startOfMonth.weekday(0);
  const endDate = endOfMonth.weekday(6);

  let day = startDate.clone();
  while (day.isBefore(endDate) || day.isSame(endDate)) {
    days.push(day.clone());
    day = day.add(1, "day");
  }

  const toggleHoliday = (date) => {
    const key = date.format("YYYY-MM-DD");
    setHolidays(prev => {
      const exists = prev.find(h => h.date === key);
      if (exists) {
        return prev.filter(h => h.date !== key);
      } else {
        return [...prev, { date: key, name: "Custom Holiday", isPublicHoliday: false, source: "custom" }];
      }
    });
  };

  const handleMonthChange = (value) => {
    const monthNum = typeof value === "string" ? parseInt(value, 10) : value;
    setCurrentMonth(cm => cm.month(monthNum));
  };

  const handleYearChange = (value) => {
    const yearNum = typeof value === "string" ? parseInt(value, 10) : value;
    setCurrentMonth(cm => cm.year(yearNum));
  };

  const saveHolidaysToDB = async () => {
    if(holidays.length === 0) {
        swal.fire({
            text: "No hoildays to update!",
            icon: "warning",
            type: "warning",
        });
        return
    }

    try {
      const clean = holidays.map(h => ({
        date: h.date,
        name: typeof h.name === "string" ? h.name : h.name?.name,
        isPublicHoliday: !!h.isPublicHoliday,
        source: h.source || "custom"
      }));

      const res = await fetch(`${API_BASE_URL}update/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-access-token": localStorage.getItem("token") },
        body: JSON.stringify({ holidays: clean })
      });

      if (!res.ok) throw new Error("Save failed");
        swal.fire({
            text: "Holidays updated successfully.",
            icon: "success",
            type: "success",
        });
    } catch (err) {
      swal.fire({
        text: "Error saving holidays.",
        icon: "error",
        type: "error",
    });
    }
  };

  return (
    <>
      <Row className="GeneralHeading withBackArrow">
        <Col md={9}>
            <HeadingTwo>
            <Link className="arrow-btn" onClick={() => navigate(-1)}>
                <MdKeyboardArrowLeft />
            </Link>
            Holiday Calendar
            </HeadingTwo>
        </Col>

        <Col md={3}>
            <Button variant="contained" fullWidth color="primary" onClick={saveHolidaysToDB}>
                Save Holidays
            </Button>
        </Col>
      </Row>

      <Row className="GeneralHeading mt-2">
        <Card sx={{ margin: "0 auto" }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <MdOutlineNavigateBefore
                size={30}
                color="red"
                style={{ cursor: "pointer" }}
                onClick={() => setCurrentMonth(cm => cm.subtract(1, "month"))}
              />

              <Box display="flex" gap={2}>
                <FormControl size="small">
                  <InputLabel>Month</InputLabel>
                  <Select value={currentMonth.month()} label="Month" onChange={e => handleMonthChange(e.target.value)}>
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i} value={i}>{dayjs().month(i).format("MMMM")}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <InputLabel>Year</InputLabel>
                  <Select value={currentMonth.year()} label="Year" onChange={e => handleYearChange(e.target.value)}>
                    {Array.from({ length: 11 }, (_, i) => (
                      <MenuItem key={i} value={currentYear - 5 + i}>{currentYear - 5 + i}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <MdOutlineNavigateNext
                size={30}
                color="red"
                style={{ cursor: "pointer" }}
                onClick={() => setCurrentMonth(cm => cm.add(1, "month"))}
              />
            </Box>

            <Grid container spacing={1} textAlign="center" fontWeight="bold" mb={1}>
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                <Grid item xs={1.7} key={d}>
                  <Typography fontWeight="600">{d}</Typography>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={1} display="flex" justifyContent="space-between" alignItems="stretch">
              {days.map(d => {
                const key = d.format("YYYY-MM-DD");
                const holiday = holidays && holidays?.find(h => h?.date === key);

                return (
                  <Grid item xs={1.7} key={key}>
                    <Box component="div" sx={{ '&:active': { transform: 'scale(0.95)' } }}>
                      <Paper
                        onClick={() => toggleHoliday(d)}
                        elevation={holiday ? 6 : 1}
                        sx={{
                          p: 1.5,
                          height: "10vh",
                          borderRadius: 2,
                          cursor: "pointer",
                          bgcolor: holiday ? "#ffcdd2" : "white",
                          border: holiday ? "2px solid #ef5350" : "1px solid #ddd",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="h6">{d.date()}</Typography>
                        {holiday && (
                          <Typography variant="caption" color="error" fontWeight="bold" textAlign="center">
                            {holiday.name}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      </Row>
    </>
  );
}